// ── EVIDENCE P1.2 — a control's evidence location, on its Evidence tab ──────────────────────────
//
// ⚠ "LOCATION EXISTS" AND "EVIDENCE EXISTS" ARE TWO LINES, NEVER ONE WORD. A provisioned folder with
//   nothing in it reads "Folder: connected · Evidence: none yet" — never "Done", never a green tick
//   beside the control. A folder is somewhere to put evidence; it is not evidence.
//
// ⓘ The action calls the synchronous single-control route (useProvisionControlEvidence), not a queued
//   run. ⚠ Box GA S3 — that route and the bulk bar's queued run call the SAME provisioning engine, so
//   there is one mechanism, entered two ways.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FolderOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useEvidenceLibraryStatus, useEvidenceLocation, useProvisionControlEvidence } from '@/api/hooks/useEvidenceProvisioning';
import { useControlEvidenceArtifacts } from '@/api/hooks/useEvidenceArtifacts';
import { ASSIGN_BTN } from './PeopleLanes';
import { PROVISIONING_ACTION, apiErrorMessage } from './provisioning-copy';
import { EvidenceUpdateCheck } from './EvidenceUpdateCheck';

/** ⚠ Its own words: "Create" on a row that already exists and failed would misdescribe the act. */
const RETRY_ACTION = 'Retry creating the folder';

export const runRecordPath = (runId: string) => `/compliance/workspace/evidence/runs/${runId}`;

import { REPOSITORY_LABELS as REPO_LABELS } from '@/api/types/evidence-repository.labels';

/**
 * ⚠ EVERY KNOWN STATE IS NAMED, AND THE UNKNOWN ONE IS NEUTRAL.
 *
 * The previous form was a ternary chain ending in `: 'being provisioned'` — so ANY state it did
 * not enumerate rendered as work-in-progress. `adopted` (V1060) and `provisioning` (V1062) both
 * fell through it: a tenant who had pointed VeriGRC at their OWN folder was told to wait for
 * something that would never happen (B-EVIDENCE-ADOPTED-RENDERS-AS-PROVISIONING).
 *
 * ⚠ A fallback must never assert progress. An unrecognised state is a fact we do not have, and
 * the honest render says so rather than guessing the most reassuring option.
 */
export function stateLabel(state: string): string {
  switch (state) {
    case 'pending':      return 'queued — not started yet';
    case 'provisioning': return 'being created';
    case 'provisioned':  return 'connected';
    case 'failed':       return 'incomplete';
    case 'adopted':      return 'connected to a folder you manage';
    case 'archived':     return 'archived';
    default:             return `state not recognised (${state})`;
  }
}

export function EvidenceLocationPanel({ reference, canProvision }: {
  reference: string;
  /** Provisioning is a workspace-manager action; others see the status and no button. */
  canProvision: boolean;
}) {
  const { data, isLoading, isError } = useEvidenceLocation(reference);
  // ⚠ ONE SOURCE FOR "DOES EVIDENCE EXIST". This panel and the list below it answer that same
  //   question, and they used to answer it from two independently-cached endpoints — so a stale
  //   cache could put "Evidence: none yet" directly above a list of evidence. The preview caught
  //   exactly that. Co-invalidating two caches would have worked until somebody forgot; reading the
  //   SAME query cannot drift at all.
  //   ⓘ "Folder exists" and "evidence exists" remain two DISTINCT facts — they just no longer come
  //     from two different places.
  const evidenceQuery = useControlEvidenceArtifacts(reference);
  const start = useProvisionControlEvidence();
  const { data: library } = useEvidenceLibraryStatus();
  const connected = library?.state === 'connected';
  const [error, setError] = useState<string | null>(null);

  // ⚠ ONE ACTION SERVES CREATE AND RETRY. A retry is this same operation against a row that
  //   already exists and failed; a separate handler would be a second path that must stay in step,
  //   and the one used less often would drift.
  const provision = () => {
    setError(null);
    start.mutate({ reference }, {
      // ⚠ A `failed` RESULT IS NOT AN ERROR PATH. The request succeeded and is telling us the
      //   provider refused a write — so the reason is surfaced as the panel's own state, not as a
      //   red banner suggesting VeriGRC broke. The refreshed row is what the panel then renders.
      onSuccess: (r) => { if (r.state === 'failed' && r.reason) setError(r.reason); },
      onError: (err) => setError(apiErrorMessage(err, 'The evidence folder could not be created.')),
    });
  };

  const loc = data?.location ?? null;
  const evidence = evidenceQuery.data?.artifacts.length ?? 0;
  const folderLine = !loc ? 'not provisioned' : stateLabel(loc.provisioning_state);

  return (
    <div className="border-b border-border-subtle px-[16px] py-[13px]">
      <div className="flex flex-wrap items-start gap-[12px]">
        <FolderOpen className="mt-[2px] h-[15px] w-[15px] text-text-secondary" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          {isLoading && <p className="text-[12.5px] text-text-faint">Checking the evidence location…</p>}
          {isError && <p className="text-[12.5px] text-cert-warn-fg">The evidence location could not be read.</p>}
          {/* ⚠ The second fact waits for its own query: rendering "none yet" while the evidence is
              still loading would state the very thing this panel exists to get right. */}
          {data && evidenceQuery.isLoading && (
            <p className="text-[13px] text-text-primary" data-testid="evidence-location-status">
              <span>Folder: <span className={cn(!loc && 'text-text-secondary')}>{folderLine}</span></span>
              <span className="text-text-faint">{' · '}</span>
              <span className="text-text-secondary">Evidence: checking…</span>
            </p>
          )}
          {data && !evidenceQuery.isLoading && (
            // ⚠ Two separate facts, two separate spans — the second is never inferred from the first.
            <p className="text-[13px] text-text-primary" data-testid="evidence-location-status">
              <span>Folder: <span className={cn(!loc && 'text-text-secondary')}>{folderLine}</span></span>
              {/* ⚠ Real spaces in the TEXT, not margins: a screen reader or a copy reads "provisioned·Evidence"
                  as one word when the separation exists only in CSS. */}
              <span className="text-text-faint">{' · '}</span>
              <span>Evidence: <span className={cn(evidence === 0 && 'text-text-secondary')}>
                {evidence === 0 ? 'none yet' : `${evidence} linked`}
              </span></span>
            </p>
          )}
          {/* ⚠ The library name lives on its OWN line. Folded into the status line it collided with the second
              fact whenever a tenant named their library "Evidence" — "connected · Evidence · Evidence: none
              yet" — which the preview capture caught and a loose assertion had not. */}
          {/* ⚠ THE PROVIDER-NEUTRAL REPOSITORY SURFACE (AC-I). Four fixed labels, five fields
              supplied by the server-side adapter. This block contains no provider type, no
              provider string and no URL construction — a Box or S3 tenant renders through the
              exact same markup. */}
          {loc?.repository && (
            <div className="mt-[5px] space-y-[2px] text-[11.5px] text-text-faint"
                 data-testid="evidence-repository">
              <p><span className="text-text-secondary">{REPO_LABELS.provider}:</span> {loc.repository.provider}</p>
              {/* ⚠ OMITTED, NOT BLANK — and under a list-scoped grant this is the NORMAL state, not
                  an edge case. GET /sites/{siteId} is 403 without site scope, so the repository's
                  display name is legitimately unavailable and the row simply does not appear. A
                  dangling "Repository:" with nothing after it reads as a broken surface rather
                  than an absent fact. */}
              {loc.repository.repository && (
                <p><span className="text-text-secondary">{REPO_LABELS.repository}:</span> {loc.repository.repository}</p>
              )}
              {loc.repository.container && (
                <p><span className="text-text-secondary">{REPO_LABELS.container}:</span> {loc.repository.container}</p>
              )}
              {loc.repository.path && (
                <p><span className="text-text-secondary">{REPO_LABELS.path}:</span> {loc.repository.path}</p>
              )}
              {/* ⚠ NO URL, NO ACTION. An absent open_url means the provider gave us no address;
                  rendering a dead link would be worse than offering nothing. */}
              {loc.repository.open_url && (
                <p className="pt-[2px]">
                  <a
                    href={loc.repository.open_url}
                    target="_blank"
                    /* ⚠ noopener/noreferrer: a new tab opened without them can reach back through
                       window.opener and navigate the VeriGRC tab. "State intact" is not only about
                       not navigating away — it is about the other page not being able to. */
                    rel="noopener noreferrer"
                    className="text-accent-cyan hover:underline"
                  >{REPO_LABELS.open}</a>
                </p>
              )}
            </div>
          )}
          {loc?.provisioning_state === 'provisioned' && loc.provisioned_at && (
            <p className="mt-[3px] text-[11.5px] text-text-faint">
              Provisioned {new Date(loc.provisioned_at).toLocaleDateString(undefined, { dateStyle: 'medium' })}
            </p>
          )}
          {loc?.provisioning_state === 'failed' && loc.failure_reason && (
            <p className="mt-[4px] text-[12px] text-cert-warn-fg">{loc.failure_reason}</p>
          )}
          {data && !loc && library && !connected && (
            <p className="mt-[3px] text-[11.5px] text-text-secondary">
              No evidence repository connected yet.{' '}
              <Link to="/compliance/workspace/evidence/library" className="text-accent-cyan hover:underline">Connect your Evidence library</Link>
              {' '}first — then this control can be given a folder.
            </p>
          )}
          {/* ⚠ PROVIDER-NEUTRAL COPY. This previously named SharePoint and Teams and described an
              `active/` subfolder the flat Phase-1 structure does not create — wrong for every other
              provider, and wrong for this one. */}
          {data && !loc && connected && (
            <p className="mt-[3px] text-[11.5px] text-text-faint">
              Creating the folder gives this control a home in your evidence repository, with a
              README and an archive folder, so people can add evidence where they already work.
            </p>
          )}
          {/* ⚠ NO "provisioning started — view the run" LINE ANY MORE. The flow is synchronous, so
              by the time this renders the work is DONE and the row says what happened. Pointing at
              a run record would send the reader somewhere to find out something the panel already
              knows, and there is no queued run to look at. */}
          {error && <p className="mt-[6px] text-[12px] text-cert-warn-fg" role="alert">{error}</p>}
          {/* ⓘ Evidence monitoring S3 — the manual "Check for Evidence Updates" for THIS location. */}
          {data && <EvidenceUpdateCheck reference={reference} location={loc} canProvision={canProvision} connected={connected} />}
        </div>
        {/* ⚠ OFFERED ON `failed` TOO — that IS the retry (AC-G). A panel that showed "incomplete"
            with no way to act on it would report a problem and withhold the fix. `provisioning`
            offers nothing, because a second click while work is in flight is not a second attempt. */}
        {canProvision && connected && data
          && loc?.provisioning_state !== 'provisioned'
          && loc?.provisioning_state !== 'provisioning'
          && loc?.provisioning_state !== 'adopted' && (
          <button type="button" className={ASSIGN_BTN} disabled={start.isPending} onClick={provision}>
            {loc?.provisioning_state === 'failed' ? RETRY_ACTION : PROVISIONING_ACTION}
          </button>
        )}
      </div>
    </div>
  );
}
